import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { IBonoPaciente } from "../../../types/BonoPaciente";
import IPaciente from "../../../types/Paciente";
import moment from "moment";
import IServicio from "../../../types/Servicio";
import EstadoFacturacionIcon, { EstadoFacturacion, getEstadoFacturacion } from "../common/EstadoFacturacionIcon";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import { useEffect, useState } from "react";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import * as constantes from "../../../common/Constantes";
import BonoService from "../../../services/BonoService";
import { useNavigate } from "react-router-dom";
import IAlert from "../../../types/Alert";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import Utilities from "../../../common/Utilities";
import DetalleFacturaBono from "./DetalleFacturaBono";
import PacienteService from "../../../services/PacienteService";
import DetalleFactura, { IFacturaData } from "../facturarCita/DetalleFactura";
import PacientesUtils from "../../../utils/Pacientes";
import CabeceraFactura from "../facturarCita/CabeceraFactura";
import ConfigUtils from "../../../utils/Config";
import EditIcon from '@mui/icons-material/Edit';
import CambiarEntidadDialog from "../common/CambiarEntidadDialog";

interface DetalleBonoPacienteProps {
  bonoPaciente: IBonoPaciente;
  pacientes: IPaciente[];
  servicios: IServicio[];
  open: boolean;
  onClose: () => void;
  onEditar?: (bono: IBonoPaciente, factura: IFacturaCompleta) => void;
  onAnular?: (bonoId: string) => void;
  onEntidadCambiada?: (bonoAnuladoId: string, bonoNuevo: IBonoPaciente) => void;
}

const DetalleBonoPaciente = ({
  bonoPaciente,
  pacientes,
  servicios,
  open,
  onClose,
  onEditar,
  onAnular,
  onEntidadCambiada,
}: DetalleBonoPacienteProps) => {
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
  const [loading, setLoading] = useState(true);
  const [facturasBono, setFacturasBono] = useState<IFacturaCompleta[]>([]);

  const [openCompartir, setOpenCompartir] = useState(false);
  const [pacientesSeleccionados, setPacientesSeleccionados] = useState<string[]>([]);
  const [loadingCompartir, setLoadingCompartir] = useState(false);
  const [loadingDescompartir, setLoadingDescompartir] = useState<null | string>(null);
  const [pacientesLocal, setPacientesLocal] = useState<IPaciente[]>(pacientes);
  const [openDetalleBono, setOpenDetalleBono] = useState(false);
  const [openDetalleCita, setOpenDetalleCita] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<IFacturaCompleta | null>(null);
  const [citasBono, setCitasBono] = useState<Record<string, any>>({});
  const [formatedFactura, setFormatedFactura] = useState<IFacturaData | null>(null);
  const [detalleCita, setDetalleCita] = useState<{ fecha: string; hora: string }>();

  const [loadingAnular, setLoadingAnular] = useState(false);
  const [confirmAnular, setConfirmAnular] = useState(false);

  const [modalCambiarEntidad, setModalCambiarEntidad] = useState(false);
  const [loadingCambiarEntidad, setLoadingCambiarEntidad] = useState(false);

  const userRole: number = ConfigUtils.getUserRole();

  const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
        return;
    }
    setAlert({ open: false, severity: 'info', msg: '' });
  };

  useEffect(() => {
    load();
  }, [bonoPaciente]);

  useEffect(() => {
    setPacientesLocal(pacientes);
  }, [pacientes]);

  const load = async () => {
    try {
      const pacientesPromise =
        pacientes.length === 1
          ? PacienteService.getAll()
          : Promise.resolve(null);

      const facturasPromise =
        BonoService.getFacturasBono(bonoPaciente._id!);

      const [pacientesRes, facturasRes] = await Promise.all([
        pacientesPromise,
        facturasPromise,
      ]);

      if (pacientesRes) {
        setPacientesLocal(pacientesRes.data);
      }

      const citasMap: Record<string, any> = {};

      facturasRes.data.citas.forEach((c: any) => {
        citasMap[c.facturaId] = c;
      });

      setFacturasBono(facturasRes.data.facturasProcesadas);
      setCitasBono(citasMap);
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "DetalleBonoPaciente.Component.load",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetalleFacturaCita = (factura: IFacturaCompleta) => {
    const cita = citasBono[factura._id];
    if (!cita) return;

    setFormatedFactura({
      _id: factura._id,
      numero: factura.numero,
      fechaFactura: factura.fechaFactura,
      tipo: factura.tipo,
      modalidad: 'total',
      pacienteId: cita.paciente._id,
      receptor: factura.receptor,
      cita: undefined,
      servicioId: cita.citaId,
      importeBase: factura.importeBase,
      importeTotal: factura.importeTotal,
      estado: factura.estado,
      estadoPago: factura.estadoPago,
      metodoPago: factura.metodoPago!,
      conceptos: factura.conceptos,
      impuestos: factura.impuestos,
      tipoFacturacion: 'total',
      servicio: factura.servicio,
      facturaOriginalId: factura.facturaOriginalId,
      facturaOriginalRef: factura.facturaOriginalRef,
      tipoRectificacion: factura.tipoRectificacion,
      facturaRectificada: factura.facturaRectificada,
      facturaRectificadaId: factura.facturaRectificadaId,
      bonoId: factura.bonoId,
      emisor: factura.emisor
    });

    setDetalleCita({
      fecha: cita.citaStart ? new Date(cita.citaStart).toLocaleDateString("es-ES") : "",
      hora: cita.citaStart ? new Date(cita.citaStart).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "",
    });

    setOpenDetalleCita(true);
  }

  const handleOpenCompartir = () => {
    setPacientesSeleccionados([]);
    setOpenCompartir(true);
  };

  const handleCompartir = async () => {
  try {
    if (pacientesSeleccionados.length === 0) return;
      setLoadingCompartir(true);
      await BonoService.compartirBono(bonoPaciente._id!, pacientesSeleccionados);
      setAlert({ open: true, severity: "success", msg: "Bono compartido correctamente" });
      bonoPaciente.compartidoIds = [...bonoPaciente.compartidoIds, ...pacientesSeleccionados];
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = { method: 'DetalleBonoPaciente.Component.compartirBono', level: 'error', message: e.message, meta: e.response };
        LogService.logError(error);
        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
      }
    } finally {
      setOpenCompartir(false);
      setLoadingCompartir(false);
      setPacientesSeleccionados([]);
    }
  };

  const handleDescompartir = async (pacienteId: string) => {
    try {
      setLoadingDescompartir(pacienteId);
      await BonoService.descompartirBono(bonoPaciente._id!, pacienteId);
      setAlert({ open: true, severity: "success", msg: "Paciente quitado del bono correctamente" });
      bonoPaciente.compartidoIds = bonoPaciente.compartidoIds.filter(id => id !== pacienteId);
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = { method: 'DetalleBonoPaciente.Component.descompartirBono', level: 'error', message: e.message, meta: e.response };
        LogService.logError(error);
        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingDescompartir(null);
    }
  };

  const handleAnular = async () => {
    try {
      setLoadingAnular(true);
      await BonoService.anularBono(bonoPaciente._id!, facturaBono?.estado);
      if (onAnular) onAnular(bonoPaciente._id!);
      onClose();
    } catch (e: any) {
      if (e.response?.status === 409) {
        // La compra del bono había cambiado de estado en otra pantalla: se vuelven a cargar sus datos y se muestran actualizados.
        setConfirmAnular(false);
        await load();
        setAlert({ open: true, severity: 'warning', msg: e.response.data.message });
      } else if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = { method: 'DetalleBonoPaciente.Component.handleAnular', level: 'error', message: e.message, meta: e.response };
        LogService.logError(error);
        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingAnular(false);
    }
  };

  const handleCambiarEntidad = async (nuevaEntidadId: string | null) => {
    try {
      setLoadingCambiarEntidad(true);
      const response = await BonoService.cambiarEntidadBono(bonoPaciente._id!, {
        userId: ConfigUtils.getUserId(),
        entidadNuevaId: nuevaEntidadId,
        estadoEsperado: facturaBono?.estado,
      });
      const bonoNuevo: IBonoPaciente = {
        ...response.data.bonoPaciente,
        entityId: response.data.factura.entityId ?? null,
      };
      onEntidadCambiada?.(bonoPaciente._id!, bonoNuevo);
      setModalCambiarEntidad(false);
      onClose();
    } catch (e: any) {
      if (e.response?.status === 409) {
        setModalCambiarEntidad(false);
        await load();
        setAlert({ open: true, severity: 'warning', msg: e.response.data.message });
      } else if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = { method: 'DetalleBonoPaciente.Component.handleCambiarEntidad', level: 'error', message: e.message, meta: e.response };
        LogService.logError(error);
        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingCambiarEntidad(false);
    }
  };

  const paciente = pacientesLocal.find(p => p._id === bonoPaciente.pacienteId);

  const serviciosSeleccionados = servicios.filter(s =>
    bonoPaciente.servicios.includes(parseInt(s.internalID!))
  );

  const pacientesCompartidos = bonoPaciente.compartidoIds
    .map(id => {
      const p = pacientesLocal.find(pa => pa._id === id);
      return p ? `${p.nombre} ${p.apellidos}` : null;
    })
    .filter(Boolean);

  const sesionesRestantes = bonoPaciente.cantidad - bonoPaciente.usado;
  const caducado = moment(bonoPaciente.caducidad).isBefore(moment(), "day");

  const facturaBono = facturasBono.find((f) => f.tipo === "bono")!;
  const facturasSesiones = facturasBono.filter((f) => f.tipo !== "bono")!;
  const estadoFacturacion: EstadoFacturacion = getEstadoFacturacion(facturaBono?.estado, facturaBono?.estadoPago);

  return (
  <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Detalle del Bono</DialogTitle>
      {loading ? <CircularProgress style={{ margin: "20px auto" }} /> :
        <>
        <DialogContent dividers>
          <Grid container spacing={1}>

            {/* Entidad — solo si hay más de una entidad de facturación configurada */}
            {ConfigUtils.getDatosFacturacionAdicional().length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography fontWeight={600} fontSize="0.85rem">Entidad:</Typography>
                  {userRole === constantes.ROLES.ADMINISTRADOR && bonoPaciente.usado === 0 && (
                    <Tooltip title="Cambiar entidad de facturación">
                      <IconButton size="small" onClick={() => setModalCambiarEntidad(true)}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography fontSize="0.85rem">
                  {[
                    { entidadId: null, nombre: ConfigUtils.getDatosFacturacion()?.nombre || "Principal" },
                    ...ConfigUtils.getDatosFacturacionAdicional().map(e => ({ entidadId: e._id, nombre: e.nombre })),
                  ].find(e => e.entidadId === (bonoPaciente.entityId ?? null))?.nombre ?? "Principal"}
                </Typography>
              </Grid>
            )}

            {/* Paciente */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight={600} fontSize="0.85rem">Paciente:</Typography>
              <Typography fontSize="0.85rem">{paciente ? `${paciente.nombre} ${paciente.apellidos}` : "Desconocido"}</Typography>
            </Grid>

            {/* Nombre del bono */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight={600} fontSize="0.85rem">Nombre del bono:</Typography>
              <Typography fontSize="0.85rem">{bonoPaciente.nombre}</Typography>
            </Grid>

            {/* Sesiones */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight={600} fontSize="0.85rem">Sesiones:</Typography>
              <Tooltip title={`${bonoPaciente.usado} usadas · ${sesionesRestantes} restantes · ${bonoPaciente.cantidad} totales`}>
                <Typography
                  fontWeight={600}
                  fontSize="0.85rem"
                  color={
                    sesionesRestantes <= 0
                      ? "error.main"
                      : sesionesRestantes <= 2
                      ? "warning.main"
                      : "text.primary"
                  }
                >
                  {sesionesRestantes}/{bonoPaciente.cantidad}
                </Typography>
              </Tooltip>
            </Grid>

            {/* Caducidad */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight={600} fontSize="0.85rem">Caducidad:</Typography>
              <Tooltip title={caducado ? "Bono caducado" : ""}>
                <Typography fontSize="0.85rem" color={caducado ? "error.main" : "text.primary"}>
                  {moment(bonoPaciente.caducidad).format("DD/MM/YYYY")}
                </Typography>
              </Tooltip>
            </Grid>

            {/* Servicios */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight={600} fontSize="0.85rem">Servicios:</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                {serviciosSeleccionados.length > 0 ? (
                  serviciosSeleccionados.map(s => (
                    <Chip key={s.internalID} label={s.nombre} size="small" />
                  ))
                ) : (
                  <Typography fontSize="0.85rem">Ninguno</Typography>
                )}
              </Box>
            </Grid>

            {/* Compartible / Compartido con */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography fontWeight={600} fontSize="0.85rem">Compartido con:</Typography>
                {bonoPaciente.compartible && sesionesRestantes > 0 && !caducado && (
                  <Tooltip title="Añadir pacientes para compartir este bono">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={handleOpenCompartir}
                      disabled={facturaBono?.estado === 'Anulada'}
                    >
                      <PersonAddIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                {bonoPaciente.compartible ? (
                  pacientesCompartidos.length > 0 ? (
                    pacientesCompartidos.map((nombreCompleto, idx) => {
                      const pacienteId = bonoPaciente.compartidoIds[idx];
                      const puedeDescompartir = sesionesRestantes > 0 && !caducado;
                    
                      return puedeDescompartir ? (
                          <Chip
                            key={pacienteId}
                            label={nombreCompleto}
                            size="small"
                            onDelete={() => handleDescompartir(pacienteId)}
                            deleteIcon={
                              <Tooltip title="Quitar paciente del bono">
                                <span>
                                  <PersonRemoveIcon fontSize="small" />
                                </span>
                              </Tooltip>
                            }
                            disabled={loadingDescompartir === pacienteId}
                          />
                      ) : (
                        <Chip
                          key={pacienteId}
                          label={nombreCompleto}
                          size="small"
                        />
                      );
                    })
                  ) : (
                    <Typography fontSize="0.85rem">Ninguno</Typography>
                  )
                ) : (
                  <Chip label="Individual" size="small" variant="outlined" />
                )}
              </Box>
            </Grid>

            {/* Factura */}
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontWeight={600} fontSize="0.85rem">Factura asociada:</Typography>
              <EstadoFacturacionIcon estado={estadoFacturacion} onClick={() => {setOpenDetalleBono(true); setSelectedFactura(facturaBono)}} />
            </Grid>

            {/* Sesiones asociadas */}
            <Grid item xs={12}>
              <Typography fontWeight={600} fontSize="0.85rem" gutterBottom>
                Sesiones utilizadas:
              </Typography>

              {bonoPaciente.usado > 0 ? (
                <Grid container spacing={1}>
                  {facturasSesiones.map(f => {
                    const estadoFacturacion: EstadoFacturacion = getEstadoFacturacion(f.estado, f.estadoPago);
                  
                    const cita = citasBono[f._id];
                    const pacienteSesion = pacientesLocal.find(p => p._id === cita?.paciente._id);
                  
                    return (
                      <Grid item xs={12} sm={6} key={f._id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            border: '1px solid #eee',
                            borderRadius: 1,
                          }}
                        >
                          <EstadoFacturacionIcon 
                            estado={estadoFacturacion} 
                            onClick={() => { handleOpenDetalleFacturaCita(f)}}
                          />
                          <Box>
                            <Typography fontSize="0.85rem" fontWeight={500}>
                              {pacienteSesion 
                                ? `${pacienteSesion.nombre} ${pacienteSesion.apellidos}` 
                                : 'Paciente desconocido'}
                            </Typography>
                            <Typography fontSize="0.75rem" color="text.secondary">
                              {moment(cita?.citaStart).format('DD/MM/YYYY')} · {moment(cita?.citaStart).format('HH:mm')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1
                  }}
                >
                  <Typography fontSize="0.85rem" color="text.secondary">
                    No se han usado sesiones todavía
                  </Typography>

                  {userRole === constantes.ROLES.ADMINISTRADOR && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        if (caducado) {
                          setAlert({ open: true, severity: "warning", msg: "Un bono caducado no se puede anular" });
                          return;
                        }
                        setConfirmAnular(true);
                      }}
                      size="small"
                      disabled={loadingAnular || facturaBono?.estado === 'Anulada'}
                    >
                      Anular bono
                    </Button>
                  )}
                </Box>
              )}
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions>
          {onEditar && sesionesRestantes > 0 && <Button color="primary" onClick={() => onEditar(bonoPaciente, facturaBono)} disabled={facturaBono?.estado === 'Anulada'}>Editar</Button>}
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
        </>
      }
    </Dialog>

    {
      openDetalleBono &&
      <DetalleFacturaBono 
        open={openDetalleBono}
        onClose={() => {setOpenDetalleBono(false); setSelectedFactura(null)}}
        factura={selectedFactura!}
        paciente={paciente!}
        onFacturaPagada={(f) => {
          setFacturasBono(prev =>
            prev.map(fb =>
              fb._id === f._id
                ? { ...fb, estadoPago: "Pagada" }
                : fb
            )
          );
          setAlert({ open: true, severity: 'success', msg: 'Factura pagada correctamente' });
        }}
        onFacturaEmitida={(f) => {
          setFacturasBono(prev =>
            prev.map(fb =>
              fb._id === f._id
                ? { ...fb, estado: "Emitida", numero: f.numero }
                : fb
            )
          );
          setAlert({ open: true, severity: 'success', msg: 'Factura emitida correctamente' });
        }}
        onFacturaRectificada={(facturaRectificativa) => {
          setFacturasBono(prev =>
            prev.map(fb => fb._id === facturaBono?._id ? facturaRectificativa : fb)
          );
          setAlert({ open: true, severity: 'success', msg: 'Factura rectificada correctamente' });
        }}
      />
    }
    {openDetalleCita && formatedFactura && detalleCita &&
    <Dialog open={open} maxWidth="sm" fullWidth onClose={() => {setFormatedFactura(null); setDetalleCita(undefined); setSelectedFactura(null); setOpenDetalleCita(false); setSelectedFactura(null)}}>
      <CabeceraFactura 
        esFacturable={true}
        estado={formatedFactura.estado} 
        paciente={formatedFactura.estado.toLowerCase() === "emitida" ? formatedFactura.receptor : citasBono[formatedFactura._id]?.paciente} 
        cita={detalleCita}
        pacienteMenor={PacientesUtils.calcularEdad(citasBono[formatedFactura._id]?.paciente.fechaNacimiento!)! < 18 ? `${citasBono[formatedFactura._id]?.paciente.nombre} ${citasBono[formatedFactura._id]?.paciente.apellidos}` : undefined}
        emisor={formatedFactura.emisor}
      />
      <DialogContent>
        <DetalleFactura
          factura={formatedFactura}
          tipoFacturacion="total"
          citaId={formatedFactura.servicioId}
          pacienteId={formatedFactura.pacienteId}
          email={citasBono[formatedFactura._id]?.paciente.email}
          authEmail={citasBono[formatedFactura._id]?.paciente.authEmail}
          detalleCita={detalleCita}
          onFacturaPagada={() => {}}
          onAnularFactura={(_id, _motivo, devolverSesiones) => {
            setFormatedFactura(prev => prev ? { ...prev, estado: "Anulada" } : prev);
            if (devolverSesiones) {
              bonoPaciente.usado = Math.max(0, bonoPaciente.usado - 1);
            }
            load();
          }}
          onFacturaEmitida={(id, f) => {
            if (!f?.factura) return;
            const facturaId = f.factura._id;
            const numero = f.factura.numero;
            setFormatedFactura(prev => (prev ? { ...prev, estado: "Emitida", numero } : prev));
            setFacturasBono(prev =>
              prev.map(fb =>
                fb._id === facturaId
                  ? { ...fb, estado: "Emitida", numero }
                  : fb
              )
            );
          }}
          onEstadoCambiado={async (mensaje) => {
            // La factura de la sesión había cambiado de estado en otra pantalla: se cierra el detalle y se recarga el bono.
            setFormatedFactura(null);
            setDetalleCita(undefined);
            setSelectedFactura(null);
            setOpenDetalleCita(false);
            await load();
            setAlert({ open: true, severity: 'warning', msg: mensaje });
          }}
          onFacturaEliminada={() => {
            // Factura anulada sin número eliminada: se cierra el detalle y se recarga el bono (la cita deja de tener factura).
            setFormatedFactura(null);
            setDetalleCita(undefined);
            setSelectedFactura(null);
            setOpenDetalleCita(false);
            setAlert({ open: true, severity: 'success', msg: 'Factura eliminada correctamente' });
            load();
          }}
          onPagarRectificada={(f) => {
            if (!f?.factura) return;
            const estadoPago = f.factura.estadoPago as "Pendiente" | "Pagada";
            const facturaId = f.factura._id;
            const metodoPago = f.factura.metodoPago;
            setFormatedFactura(prev => prev ? { ...prev, estadoPago } : prev);
            setFacturasBono(prev =>
              prev.map(fb => fb._id === facturaId ? { ...fb, estadoPago, metodoPago } : fb)
            );
          }}
          onEmitirRectificada={(f) => {
            if (!f?.factura) return;
            const { factura: facturaActualizada } = f;
            setFormatedFactura(prev => prev ? { ...prev, estado: "Emitida", numero: facturaActualizada.numero } : prev);
            setFacturasBono(prev =>
              prev.map(fb => fb._id === facturaActualizada._id ? { ...fb, estado: "Emitida", numero: facturaActualizada.numero } : fb)
            );
          }}
          pacienteFechaNacimiento={citasBono[formatedFactura._id]?.paciente.fechaNacimiento}
          pacienteMenor={PacientesUtils.calcularEdad(citasBono[formatedFactura._id]?.paciente.fechaNacimiento!)! < 18 ? `${citasBono[formatedFactura._id]?.paciente.nombre} ${citasBono[formatedFactura._id]?.paciente.apellidos}` : undefined}
        />
      </DialogContent>
    </Dialog>
    } 

    <Dialog open={openCompartir} onClose={() => {setOpenCompartir(false)}} fullWidth maxWidth="sm">
      <DialogTitle>Selecciona pacientes para compartir</DialogTitle>
      <DialogContent sx={{ minHeight: 190 }}>
        <Autocomplete 
          multiple
          disablePortal
          autoComplete
          id="pacientes"
          value={pacientesLocal.filter(p => pacientesSeleccionados.includes(p._id))}
          options={pacientesLocal.filter(p => p._id !== bonoPaciente.pacienteId && !bonoPaciente.compartidoIds.includes(p._id))}
          noOptionsText={'No se han encontrado pacientes'}
          renderOption={(props, option) => { return (<li {...props} key={option._id}>{`${option.nombre} ${option.apellidos}`}</li>) }}
          renderInput={(params) => <TextField required {...params} label="Pacientes" variant="outlined" margin="dense" fullWidth placeholder="Buscar pacientes" />}
          onChange={(_, newValue) => { setPacientesSeleccionados(newValue.map(p => p._id!)) }}
          getOptionLabel={(option) => `${option.nombre} ${option.apellidos}` || ""}
          filterOptions={(options, params) => Utilities.filterPacientesAutocomplete(options, params.inputValue)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {setOpenCompartir(false)}}>Cancelar</Button>
        <Button color="primary" onClick={handleCompartir} disabled={pacientesSeleccionados.length === 0 || loadingCompartir}>
          Compartir
        </Button>
      </DialogActions>
    </Dialog>

    {modalCambiarEntidad && (
      <CambiarEntidadDialog
        open={modalCambiarEntidad}
        onClose={() => setModalCambiarEntidad(false)}
        entidadActualId={bonoPaciente.entityId ?? null}
        loading={loadingCambiarEntidad}
        onConfirm={handleCambiarEntidad}
      />
    )}

    {confirmAnular && (
      <Dialog open={confirmAnular} onClose={() => {setConfirmAnular(false)}}>
        <DialogTitle>Confirmar anulación de bono</DialogTitle>
        <DialogContent>
          <Typography>
            Se anulará el bono y su factura de compra.
            {facturaBono?.estado === "Emitida" && " Se emitirá una factura de anulación."}
            {" "}¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmAnular(false) }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleAnular();
              setConfirmAnular(false);
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    )}

    <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
      <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
    </Snackbar>
    </>
  );
};

export default DetalleBonoPaciente;
